import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Briefcase, Heart, Users, Leaf, Download } from "lucide-react";

const MENU_PDFS = {
  corporate: "https://customer-assets.emergentagent.com/job_beddbadf-e6fa-4f81-951f-ef17a813b92f/artifacts/emrj4qa5_Kamaliicious_Corporate_Catering_Menu.pdf",
  events: "https://customer-assets.emergentagent.com/job_beddbadf-e6fa-4f81-951f-ef17a813b92f/artifacts/nd12epcc_Kamaliicious_Full_Service_Events_Menu.pdf",
  appetizers: "https://customer-assets.emergentagent.com/job_beddbadf-e6fa-4f81-951f-ef17a813b92f/artifacts/bjjwijql_Kamaliicious_Hors_dOeuvres_Menu.pdf"
};

export const MenusSection = () => {
  const [activeTab, setActiveTab] = useState("corporate");

  return (
    <section
      id="menus"
      className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FDFBF7]"
      data-testid="menus-section"
    >
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <p className="text-sm tracking-widest uppercase font-semibold text-orange-700">
            Food that brings people together
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-gray-800">
            Menus Designed for Flexibility
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-gray-600 pt-4">
            Our menus are built to adapt — whether you're feeding a small team or hosting a large event. Choose from curated offerings or work with us to create a custom menu that aligns with your taste, dietary needs, and occasion.
          </p>
          <div className="flex items-center justify-center gap-2 pt-4">
            <Leaf className="w-5 h-5 text-green-600" />
            <p className="text-gray-600">
              Vegetarian, vegan, gluten-free, and mild-to-spicy options available.
            </p>
          </div>
          
          {/* Download Menu Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-4 pt-6">
            <a href={MENU_PDFS.corporate} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-orange-700 text-orange-700 hover:bg-orange-50 rounded-full" data-testid="download-corporate-menu">
                <Download className="w-4 h-4 mr-2" />
                Corporate Menu PDF
              </Button>
            </a>
            <a href={MENU_PDFS.events} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-orange-700 text-orange-700 hover:bg-orange-50 rounded-full" data-testid="download-events-menu">
                <Download className="w-4 h-4 mr-2" />
                Events Menu PDF
              </Button>
            </a>
            <a href={MENU_PDFS.appetizers} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-orange-700 text-orange-700 hover:bg-orange-50 rounded-full" data-testid="download-appetizers-menu">
                <Download className="w-4 h-4 mr-2" />
                Appetizers Menu PDF
              </Button>
            </a>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full max-w-lg mx-auto grid grid-cols-3 bg-white border border-stone-200 rounded-full p-1 shadow-sm mb-12">
            <TabsTrigger
              value="corporate"
              className="rounded-full data-[state=active]:bg-orange-700 data-[state=active]:text-white transition-colors"
              data-testid="menu-tab-corporate"
            >
              <Briefcase className="w-4 h-4 mr-2 hidden sm:inline" />
              Corporate
            </TabsTrigger>
            <TabsTrigger
              value="weddings"
              className="rounded-full data-[state=active]:bg-orange-700 data-[state=active]:text-white transition-colors"
              data-testid="menu-tab-weddings"
            >
              <Heart className="w-4 h-4 mr-2 hidden sm:inline" />
              Weddings
            </TabsTrigger>
            <TabsTrigger
              value="private"
              className="rounded-full data-[state=active]:bg-orange-700 data-[state=active]:text-white transition-colors"
              data-testid="menu-tab-private"
            >
              <Users className="w-4 h-4 mr-2 hidden sm:inline" />
              Private
            </TabsTrigger>
          </TabsList>

          {/* Corporate Tab */}
          <TabsContent value="corporate" className="mt-0">
            <div className="text-center mb-8">
              <h3 className="font-heading text-2xl font-semibold text-gray-800">
                Corporate Menu
              </h3>
              <p className="text-gray-600 mt-2">Professional catering solutions for modern workplaces</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h4 className="font-heading text-xl font-semibold text-gray-800">Executive Lunch</h4>
                <p className="text-sm text-gray-600 mt-2">Premium selection for executive meetings</p>
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Choice of 2 Entrées</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Fresh Salad Bar</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Artisan Breads</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Dessert Selection</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Beverages Included</span></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h4 className="font-heading text-xl font-semibold text-gray-800">Team Lunch</h4>
                <p className="text-sm text-gray-600 mt-2">Hearty meals for team gatherings</p>
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Rotating Daily Specials</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Vegetarian Options</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Rice & Naan</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Sweet Ending</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Bulk Pricing Available</span></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h4 className="font-heading text-xl font-semibold text-gray-800">Working Lunch Boxes</h4>
                <p className="text-sm text-gray-600 mt-2">Individual portions for busy days</p>
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Sealed Fresh</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Cutlery Included</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Dietary Labels</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Customizable</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Easy Distribution</span></div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Weddings Tab */}
          <TabsContent value="weddings" className="mt-0">
            <div className="text-center mb-8">
              <h3 className="font-heading text-2xl font-semibold text-gray-800">
                Wedding Menu
              </h3>
              <p className="text-gray-600 mt-2">Create unforgettable memories with exquisite cuisine</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h4 className="font-heading text-xl font-semibold text-gray-800">Grand Feast</h4>
                <p className="text-sm text-gray-600 mt-2">Full-service wedding catering</p>
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">5-Course Meal</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Live Stations</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Custom Dessert Bar</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Premium Beverages</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">White-Glove Service</span></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h4 className="font-heading text-xl font-semibold text-gray-800">Cocktail Reception</h4>
                <p className="text-sm text-gray-600 mt-2">Elegant passed appetizers and drinks</p>
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">12+ Appetizer Varieties</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Signature Drinks</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Chef's Presentation</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Grazing Tables</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Late Night Snacks</span></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h4 className="font-heading text-xl font-semibold text-gray-800">Intimate Celebration</h4>
                <p className="text-sm text-gray-600 mt-2">Perfect for smaller ceremonies</p>
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Personalized Menu</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Family-Style Service</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Signature Dishes</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Custom Wedding Cake</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Beverage Pairing</span></div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Private Tab */}
          <TabsContent value="private" className="mt-0">
            <div className="text-center mb-8">
              <h3 className="font-heading text-2xl font-semibold text-gray-800">
                Private Events
              </h3>
              <p className="text-gray-600 mt-2">Tailored menus for personal celebrations</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h4 className="font-heading text-xl font-semibold text-gray-800">Birthday Bash</h4>
                <p className="text-sm text-gray-600 mt-2">Celebrate milestones in style</p>
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Customized Menu</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Theme-Based Dishes</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Birthday Cake</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Kids' Options</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Party Platters</span></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h4 className="font-heading text-xl font-semibold text-gray-800">Festival Feast</h4>
                <p className="text-sm text-gray-600 mt-2">Traditional celebrations done right</p>
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Authentic Recipes</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Festive Sweets</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Ritual-Friendly</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Family Portions</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Traditional Setup</span></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h4 className="font-heading text-xl font-semibold text-gray-800">Cocktail Party</h4>
                <p className="text-sm text-gray-600 mt-2">Sophisticated entertaining</p>
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Passed Hors d'Oeuvres</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Grazing Boards</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Beverage Pairing</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Elegant Setup</span></div>
                  <div className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-orange-300" /><span className="text-gray-700">Staff Included</span></div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default MenusSection;
